import { EventEmitter, Injectable, Type } from "@angular/core";
import { Cartesian3 } from "cesium";
import { Subscription } from "rxjs";
import { PickOptions } from "../models/pickOptions.enum";
import { DisposableAndPausableObservable, DtmCesiumEvent } from "./pointer-manager/cesium-events.model";
import { DtmMapEventsManagerService } from "./pointer-manager/map-events-manager.service";

/* eslint-disable no-underscore-dangle */

export interface ContextMenuOptions<Data = unknown> {
    data?: Data;
    closeOnLeftClick?: boolean;
    closeOnLeftClickPriority?: number;
}

export type BasicContextMenu = Type<{
    data?: unknown;
}>;

/**
 * NOTE: this service is update version extracted from angular-cesium
 *
 *  types where updated to make it more strict
 *
 * The Service manages a singleton context menu over the map. It should be initialized with MapEventsManager.
 * The Service allows opening and closing of the context menu and passing data to the context menu inner component.
 *
 * notice, `data` will be injected to your custom menu component into the `data` field in the component.
 * __Usage :__
 * ```
 *  ngOnInit() {
 *   this.clickEvent$ = this.eventsManager.register({ event: CesiumEvent.RIGHT_CLICK, pick: PickOptions.PICK_ONE });
 *   this.clickEvent$.subscribe(result => {
 *    if (result.entities) {
 *      const pickedMarker = result.entities[0];
 *      this.contextMenuService.open(MapContextmenuComponent, pickedMarker.position, {
 *        data: {
 *          myData: data,
 *          onDelete: () => this.delete(pickedMarker.id)
 *        }
 *      });
 *    }
 *   });
 *  }
 *
 *
 *  private delete(id) {
 *    this.mapMenu.close();
 *    this.detailedSiteService.removeMarker(id);
 *  }
 * ```
 */
@Injectable()
export class ContextMenuService {
    private _showContextMenu = false;
    private _options: ContextMenuOptions | undefined;
    private _position: Cartesian3 | undefined;
    private _content: BasicContextMenu | undefined;
    private mapEventsManager!: DtmMapEventsManagerService;
    private leftClickRegistration!: DisposableAndPausableObservable<unknown> | undefined;
    private leftClickSubscription!: Subscription | undefined;
    private _contextMenuChangeNotifier = new EventEmitter();
    private _onOpen = new EventEmitter();
    private _onClose = new EventEmitter();
    private _defaultContextMenuOptions: ContextMenuOptions = {
        closeOnLeftClick: true,
        closeOnLeftClickPriority: 10,
    };

    public get contextMenuChangeNotifier(): EventEmitter<void> {
        return this._contextMenuChangeNotifier;
    }

    public get showContextMenu(): boolean {
        return this._showContextMenu;
    }

    public get options(): ContextMenuOptions | undefined {
        return this._options;
    }

    public get position(): Cartesian3 | undefined {
        return this._position;
    }

    public get content(): BasicContextMenu | undefined {
        return this._content;
    }

    public get onOpen(): EventEmitter<void> {
        return this._onOpen;
    }

    public get onClose(): EventEmitter<void> {
        return this._onClose;
    }

    public init(mapEventsManager: DtmMapEventsManagerService) {
        this.mapEventsManager = mapEventsManager;
    }

    public open<Data>(contentComponent: BasicContextMenu | undefined, position: Cartesian3, options: ContextMenuOptions<Data> = {}) {
        this.close();
        this._content = contentComponent;
        this._position = position;
        this._options = Object.assign({}, this._defaultContextMenuOptions, options);
        this._showContextMenu = true;
        if (this.mapEventsManager && this._options.closeOnLeftClick) {
            this.leftClickRegistration = this.mapEventsManager.register({
                event: DtmCesiumEvent.LEFT_CLICK,
                pick: PickOptions.NO_PICK,
                priority: this._options.closeOnLeftClickPriority,
            });
            this.leftClickSubscription = this.leftClickRegistration.subscribe(() => {
                this.leftClickSubscription?.unsubscribe();
                this.close();
            });
        }

        this._onOpen.emit();
        this._contextMenuChangeNotifier.emit();
    }

    public close() {
        this._content = undefined;
        this._position = undefined;
        this._options = undefined;
        this._showContextMenu = false;
        if (this.leftClickRegistration) {
            this.leftClickRegistration.dispose();
            this.leftClickRegistration = undefined;
        }
        if (this.leftClickSubscription) {
            this.leftClickSubscription.unsubscribe();
            this.leftClickSubscription = undefined;
        }

        this._onClose.emit();
        this._contextMenuChangeNotifier.emit();
    }
}
